import { Injectable } from '@angular/core';
import { DESKTOP_DEVICES, DEVICES, DeviceDetectorService } from 'ngx-device-detector';

/**
 * Replaces DeviceDetectorService app-wide so that tablets are never "neither mobile nor desktop".
 * ngx-device-detector reports an iPad/Android-tablet user agent as tablet only, which leaves
 * `isMobile()` and `isDesktop()` both false and pages render without sidebar or bottom menu.
 *
 * Rule: desktop if the user agent is a desktop, or a tablet whose short screen side is >= 1000pt
 * (13" iPads, same rule as the iOS app); everything else is mobile.
 */
@Injectable()
export class BlancDeviceDetectorService extends DeviceDetectorService {

  private static readonly LARGE_TABLET_MIN_SHORT_SIDE = 1000;

  isDesktop(userAgent: string = this.userAgent): boolean {
    return this.libraryIsDesktop(userAgent) || (super.isTablet(userAgent) && this.isLargeTablet());
  }

  isMobile(userAgent: string = this.userAgent): boolean {
    return !this.isDesktop(userAgent);
  }

  /**
   * Same logic as DeviceDetectorService.isDesktop(), but it calls the library's own isMobile()
   * through `super`. The library version calls `this.isMobile()`, which is overridden above and
   * would call isDesktop() again: infinite recursion whenever the device is unknown.
   */
  private libraryIsDesktop(userAgent: string): boolean {
    if (this.device === DEVICES.UNKNOWN && (super.isMobile(userAgent) || super.isTablet(userAgent))) {
      return false;
    }
    return DESKTOP_DEVICES.indexOf(this.device) > -1;
  }

  private isLargeTablet(): boolean {
    if (typeof window === 'undefined' || !window.screen) {
      return false;
    }
    return Math.min(window.screen.width, window.screen.height) >= BlancDeviceDetectorService.LARGE_TABLET_MIN_SHORT_SIDE;
  }
}
